/**
 * Centralised UI terminology — single source of truth for user-facing
 * names that differ from backend identifiers.
 *
 *   • Roles: backend ids (`entity_admin`, …) → display names. Never
 *     shown with extra descriptions ("· full control" etc).
 *   • "Tenant" is shown as "Entity" everywhere in the UI. Backend
 *     terminology, API fields (`tenant_id`), DB schemas, RBAC keys and
 *     internal identifiers are UNCHANGED — this is display-only.
 *
 * Use `roleLabel(role)` for any role text and `uiTerm(str)` for any
 * server-originated message shown to a user (API errors / notices).
 */

export const ROLE_ORDER = ["super_admin", "org_admin", "entity_admin", "entity_viewer", "viewer"];

export const ROLE_LABELS = {
    super_admin: "Super Admin",
    org_admin: "Org Admin",
    entity_admin: "Entity Admin",
    entity_viewer: "Entity Viewer",
    viewer: "Viewer",
};

/** Exact display name for a backend role id (falls back to Title Case). */
export function roleLabel(role) {
    if (!role) return "";
    if (ROLE_LABELS[role]) return ROLE_LABELS[role];
    return String(role).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** `[{ value, label }]` role options in canonical order. */
export function roleOptions(roles = ROLE_ORDER) {
    return roles.map((r) => ({ value: r, label: roleLabel(r) }));
}

// Display terminology map (backend word → UI word), case-preserving.
// Order matters: plural / longer forms first.
const TERMS = [
    [/\bTENANTS\b/g, "ENTITIES"],
    [/\bTENANT\b/g, "ENTITY"],
    [/\bTenants\b/g, "Entities"],
    [/\bTenant\b/g, "Entity"],
    [/\btenants\b/g, "entities"],
    [/\btenant\b/g, "entity"],
];

/**
 * Rewrite backend terminology for display ("Tenant not found" →
 * "Entity not found"). Word-boundary matching keeps identifiers such as
 * `tenant_id` untouched. Non-strings are returned as-is.
 */
export function uiTerm(value) {
    if (typeof value !== "string" || !/tenant/i.test(value)) return value;
    let out = value;
    for (const [re, rep] of TERMS) out = out.replace(re, rep);
    return out;
}

/** Apply `uiTerm` to the human-readable fields of an API payload. */
export function uiTermPayload(data) {
    if (!data || typeof data !== "object") return data;
    for (const k of ["detail", "message", "error_message", "reason", "hint"]) {
        if (typeof data[k] === "string") data[k] = uiTerm(data[k]);
    }
    if (data.detail && typeof data.detail === "object" && !Array.isArray(data.detail)) {
        for (const k of ["message", "detail", "reason"]) {
            if (typeof data.detail[k] === "string") data.detail[k] = uiTerm(data.detail[k]);
        }
    }
    if (Array.isArray(data.detail)) {
        data.detail = data.detail.map((d) => (d && typeof d.msg === "string" ? { ...d, msg: uiTerm(d.msg) } : d));
    }
    return data;
}

// Keys whose string VALUE is user-facing display text (never an id,
// slug, enum or code identifier). Deep traversal below only rewrites
// strings sitting under one of these keys, so `tenant_id` payload fields
// remain untouched while a backend-authored `hint: "Tenants whose trial…"`
// becomes "Entities whose trial…" on screen.
const DISPLAY_KEYS = new Set([
    "hint", "message", "detail", "reason", "error_message", "description",
    "title", "subtitle", "summary", "label", "tooltip", "placeholder",
    "help", "note", "text", "body", "cta", "cta_label", "cta_text",
    "action_label", "action_text", "banner", "disclaimer",
]);

const MAX_DEPTH = 6;

function deepRewriteDisplay(node, depth) {
    if (depth > MAX_DEPTH || node == null) return node;
    if (Array.isArray(node)) {
        for (let i = 0; i < node.length; i++) deepRewriteDisplay(node[i], depth + 1);
        return node;
    }
    if (typeof node !== "object") return node;
    for (const k of Object.keys(node)) {
        const v = node[k];
        if (typeof v === "string") {
            if (DISPLAY_KEYS.has(k)) node[k] = uiTerm(v);
        } else if (v && typeof v === "object") {
            deepRewriteDisplay(v, depth + 1);
        }
    }
    return node;
}

/**
 * Walk a successful response payload and rewrite "tenant" → "entity"
 * only in strings sitting under a well-known display-prone key. Safe to
 * call on any response: non-string values are skipped and the known
 * display keys never collide with identifier fields such as `tenant_id`.
 */
export function uiTermDeep(data) {
    if (!data || typeof data !== "object") return data;
    return deepRewriteDisplay(data, 0);
}
